import { getEnvSettings } from '../configurationServices'
import { getFingerprint } from 'src/utils/fingerprint'

const { REACT_APP_PB_API_LOTTERY_BASE_URL, OIDC_STORAGE_KEY } = getEnvSettings()

export interface TicketSaleTransaction {
  cant_tickets?: string
  cod_solicitud_facturacion?: string
  cod_transaccion?: string
  cod_vendedor?: string
  distribuidor?: string
  fecha_hora_sorteo?: string
  fecha_hora_transaccion?: string
  no_sorteo: string
  no_trans?: string
  nombre_sorteo: string
  tipo_sorteo?: string
  total_factura?: string
  vendedor?: string
  cod_anulacion?: string
  fecha_hora_anulacion?: string
  cod_solicitud_anulacion?: string
  // Fields for inquiry response
  cant_fracciones?: string
  cod_pago?: string
  estado_pago_boleto?: string
  estado_sorteo?: number
  estado_sorteo_str?: string
  fracc_desde?: string
  fracc_hasta?: string
  no_boleto?: string
  no_control?: string
  no_ticket?: string
}

export interface TicketSaleTicket {
  cant_fracciones?: string
  cod_anulacion?: string
  cod_ganador?: string
  cod_pago?: string
  codigo_barra?: string
  fracc_desde?: string
  fracc_hasta?: string
  no_boleto?: string
  no_control?: string
  no_ticket?: string
  precio_fraccion?: string
  total?: string
  fecha_hora_anulacion?: string
  // Fields for inquiry response (prize info)
  estado_pago_ticket?: string
  nombre_premio?: string
  total_pagar?: string
  valor_premio?: string
}

/**
 * Interface for ticket details from Placebet system
 */
export interface TicketDetails {
  agencyId?: string
  amount?: number
  createdAt?: string
  currency?: string
  enterpriseId?: string
  id?: string
  number?: string
  product?: string
  requestCode?: string
  status?: string
  workstationId?: string
}

/**
 * Interface for ticket sales response
 */
export interface TicketSaleResponse {
  codigo?: string
  mensaje?: string | null
  datos_trans?: TicketSaleTransaction | TicketSaleTransaction[]
  tickets?: TicketSaleTicket[]
  ticketDetails?: TicketDetails
  total_pagar?: number
  ticket_status?: string
}

export class TicketSaleError extends Error {
  code: string

  constructor(message: string, code: string) {
    super(message)
    this.name = 'TicketSaleError'
    this.code = code
  }
}

interface OidcUser {
  access_token: string
  token_type: string
  expires_at: number
}

/**
 * Gets the stored token from session storage using OIDC key
 */
const getStoredToken = (): string | null => {
  try {
    const storedData = sessionStorage.getItem(OIDC_STORAGE_KEY)

    if (!storedData) {
      console.error('getStoredToken - No OIDC data found in session storage')
      return null
    }

    const oidcUser: OidcUser = JSON.parse(storedData)
    return oidcUser?.access_token || null
  } catch (error) {
    console.error('getStoredToken - Error parsing OIDC data:', error)
    return null
  }
}

/**
 * Inquires about a lottery ticket
 */
export const inquiryTicket = async (ticketNumber: string): Promise<TicketSaleResponse> => {
  const token = getStoredToken()

  if (!token) {
    throw new Error('Token no encontrado en session storage')
  }

  const fingerprint = await getFingerprint()

  const response = await fetch(`${REACT_APP_PB_API_LOTTERY_BASE_URL}/api/inquery/ticketStatus`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-fingerprint': fingerprint,
      'x-placebet-device-metadata':
        'ewogICJpcExvY2F0aW9uIjogewogICAgImNvdW50cnkiOiB7CiAgICAgICJjb2RlIjogIkRPIiwKICAgICAgIm5hbWUiOiAiRG9taW5pY2FuIFJlcHVibGljIgogICAgfQogIH0sCiAgInJlcXVlc3RJZCI6ICJsb3R0ZXJ5VGlja2V0LXRpY2tldFN0YXR1cyIKfQ==',
    },
    body: JSON.stringify({ ticket_number: ticketNumber, fingerprint: 'admin' }),
  })

  let responseData: TicketSaleResponse

  try {
    responseData = await response.json()
  } catch {
    throw new TicketSaleError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'DESCONOCIDO' : `${response.status}`,
    )
  }

  // Check if response is ok
  if (!response.ok && !responseData.codigo) {
    throw new TicketSaleError(`Error al consultar el ticket (${response.status})`, `${response.status}`)
  }

  // Check for error codes in the response
  // if (responseData.codigo && responseData.codigo !== '201') {
  //   const errorMessage = responseData.mensaje || 'Error al consultar el ticket'
  //   throw new TicketSaleError(errorMessage, responseData.codigo)
  // }

  // Normalize datos_trans to always be an object (not array) for inquiry responses
  if (responseData.datos_trans && Array.isArray(responseData.datos_trans)) {
    responseData.datos_trans = responseData.datos_trans[0]
  }

  // Check if tickets array is empty and there's an error message
  if (!responseData.ticketDetails) {
    const errorMessage = responseData.mensaje || 'No se encontró información del ticket'
    const errorCode = responseData.codigo || 'DESCONOCIDO'
    throw new TicketSaleError(errorMessage, errorCode)
  }
  // if ((!responseData.tickets || responseData.tickets.length === 0) && !responseData.datos_trans) {
  //   const errorMessage = responseData.mensaje || 'No se encontró información del ticket'
  //   const errorCode = responseData.codigo || 'DESCONOCIDO'
  //   throw new TicketSaleError(errorMessage, errorCode)
  // }

  return responseData
}
